/**
 * PreviewCanvas — Center panel.
 * Renders all sections in email-width wrapper with live A4 proportions.
 * Also provides the offscreen A4 renderer used by the export pipeline.
 */
import React, { forwardRef, useEffect } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { useEditorStore } from '../stores/editorStore';
import { SectionBlock } from './SectionBlock';

export const PreviewCanvas = forwardRef(function PreviewCanvas({ readOnly = false }, ref) {
  const { sections, meta, selectedSectionId, reorderSections, selectSection } = useEditorStore();

  // Escape key → deselect
  React.useEffect(() => {
    if (readOnly) return;
    const handler = (e) => {
      if (e.key === 'Escape' && selectedSectionId) selectSection(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [readOnly, selectedSectionId, selectSection]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    reorderSections(arrayMove(sections, oldIdx, newIdx));
  };

  const width = meta.contentWidth || 680;
  const bg    = meta.backgroundColor || '#f3f4f6';
  const font  = meta.fontFamily || 'Inter, sans-serif';

  return (
    <div
      style={{
        background: bg,
        display: 'flex', justifyContent: 'center',
        padding: '24px 16px',
        minHeight: 'calc(100vh - 52px)',
      }}
      onClick={() => !readOnly && selectedSectionId && selectSection(null)}
    >
      {/* A4-width content wrapper — also the export capture target */}
      <div
        ref={ref}
        style={{
          width: '100%', maxWidth: width,
          background: 'var(--em-bg-card)',
          fontFamily: font,
          boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
          borderRadius: 8,
          overflow: 'hidden',
          minHeight: 400,
        }}
      >
        {sections.length === 0 ? (
          <div style={{
            padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 14,
          }}>
            <p style={{ marginBottom: 8, fontSize: 32 }}>✉️</p>
            <p>Your newsletter is empty.</p>
            <p style={{ fontSize: 12, marginTop: 4 }}>Add sections from the left panel to get started.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {sections.map((section) => (
                <SectionBlock
                  key={section.id}
                  section={section}
                  isSelected={!readOnly && section.id === selectedSectionId}
                  readOnly={readOnly}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
});
